import React, { useState, useEffect } from 'react';
import { MessageSquare } from 'lucide-react';
import { UnpaidInvoice, AgentCustomerAnalysis } from '../../types/organizations';
import { getCategoryStyling } from '../../utils/categoryStyling';
import { getLatestAnalysis } from '../../utils/analysis';
import { notesService } from '../../services/notesService';
import { useUser } from '../../context/UserContext';
import NotesModal from '../common/modals/NotesModal';

interface InvoiceCardProps {
  invoice: UnpaidInvoice;
  onViewAnalyses: (type: string, analyses: any[]) => void;
  organizationId: string;
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getStatusStyling = (status: string | null | undefined) => {
  if (!status) {
    return 'bg-gray-100 text-gray-800';
  }
  
  switch (status.toUpperCase()) {
    case 'PAID':
      return 'bg-green-100 text-green-800';
    case 'AUTHORISED':
      return 'bg-blue-100 text-blue-800';
    case 'DRAFT':
      return 'bg-gray-100 text-gray-800';
    case 'SUBMITTED':
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const calculateDelay = (paidDate: string, dueDate: string) => {
  const paid = new Date(paidDate);
  const due = new Date(dueDate);
  const diffTime = paid.getTime() - due.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'At due date';
  } else if (diffDays > 0) {
    return `${diffDays} days after due`;
  } else {
    return `${Math.abs(diffDays)} days before due`;
  }
};

// use shared getLatestAnalysis from utils/analysis

// Helper function to get customer insight category
const getCustomerInsightCategory = (invoice: UnpaidInvoice): string | null => {
  const insight = getLatestAnalysis(invoice.customer_analyses, 'customer_insight');
  return insight?.output_json?.selected_category || null;
};


// Helper function to get payment reliability score
const getPaymentReliabilityScore = (invoice: UnpaidInvoice): { score: number; confidence: string } | null => {
  const analysis = getLatestAnalysis(invoice.customer_analyses, 'payment_reliability_score');
  if (!analysis?.output_json) return null;

  const score = typeof analysis.output_json.score === 'string'
    ? parseInt(analysis.output_json.score) || 0
    : analysis.output_json.score || 0;
  const confidence = analysis.output_json.confidence || 'Unknown';

  // Convert confidence to shorter form
  const shortConfidence = confidence === 'Medium' ? 'Mid' : confidence;

  return {
    score,
    confidence: shortConfidence
  };
};

const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice, onViewAnalyses, organizationId }) => {
  const { accessToken, setUserAndToken } = useUser();
  const [notesCount, setNotesCount] = useState(0);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const isPaid = invoice.status?.toUpperCase() === 'PAID';
  const isUnpaid = !isPaid;
  
  // Get customer analysis data
  const insightCategory = getCustomerInsightCategory(invoice);
  const paymentScore = getPaymentReliabilityScore(invoice);
  const categoryStyling = insightCategory ? getCategoryStyling(insightCategory) : null;

  // Load notes count from invoice data
  useEffect(() => {
    if (invoice.invoice_id) {
      // Use note_count from invoice data if available, otherwise default to 0
      setNotesCount(invoice.note_count || 0);
    }
  }, [invoice.invoice_id, invoice.note_count]);

  const handleNotesClick = () => {
    setShowNotesModal(true);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
      {/* Invoice Header */}
      <div className="mb-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h4 className="text-lg font-semibold text-gray-900 truncate">Invoice #{invoice.invoice_number || 'N/A'}</h4>
            
            {/* Latest Note Preview */}
            {invoice.latest_note && (
              <div className="mt-1 flex items-center">
                <div className="w-1 h-4 bg-gray-400 mr-3 flex-shrink-0"></div>
                <span className="text-sm text-gray-600 italic line-clamp-2 flex-1">
                  {invoice.latest_note.content}
                </span>
              </div>
            )}
          </div>
          {/* Notes Button */}
          <button
            onClick={handleNotesClick}
            className={`p-2 rounded-lg transition-colors ${
              notesCount > 0 
                ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
            }`}
            title={notesCount > 0 ? `${notesCount} note${notesCount !== 1 ? 's' : ''}` : 'Add note'}
          >
            <MessageSquare className="w-4 h-4" />
          </button>
        </div>
        
        
        <div className="flex items-center space-x-2 mb-1">
          <p className="text-sm text-gray-500">{invoice.contact?.name || 'Unknown Contact'}</p>
          {/* Customer Category Badge - moved next to customer name */}
          {insightCategory && categoryStyling && (
            <div className={`px-2 py-1 ${categoryStyling.bg} ${categoryStyling.text} text-xs font-medium rounded-full flex-shrink-0`}>
              {insightCategory.replace(/customer/gi, '').trim()}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-400">{invoice.reference || ''}</p>
      </div>

      {/* Invoice Details */}
      <div className="space-y-2 mb-4">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">{isPaid ? 'Amount:' : 'Amount Due:'}</span>
          <span className="text-lg font-semibold">
            ${isPaid ? (invoice.total?.toLocaleString() || '0') : (invoice.amount_due?.toLocaleString() || '0')}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Status:</span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusStyling(invoice.status)}`}>
            {invoice.status || 'Unknown'}
          </span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Due Date:</span>
          <span className="text-sm font-medium text-gray-900">
            {invoice.due_date_at ? new Date(invoice.due_date_at).toLocaleDateString() : 'N/A'}
          </span>
        </div>
        
        {/* Paid Date with Delay - only show for PAID invoices */}
        {isPaid && invoice.fully_paid_on_date && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Paid:</span>
            {(() => {
              if (!invoice.fully_paid_on_date || !invoice.due_date_at) return 'N/A';
              const paid = new Date(invoice.fully_paid_on_date);
              const due = new Date(invoice.due_date_at);
              const diffDays = Math.ceil((paid.getTime() - due.getTime()) / (1000 * 60 * 60 * 24));
              const isAfterDue = diffDays > 0;
              if (!Number.isFinite(diffDays)) return null;
              return (
                <span
                  className={
                    `text-sm font-medium px-2 py-0.5 rounded-full ` +
                    (isAfterDue ? 'bg-amber-100 text-amber-800' : 'text-gray-600')
                  }
                >
                  {diffDays === 0
                    ? 'At due date'
                    : diffDays > 0
                      ? `${diffDays} days after due`
                      : `${Math.abs(diffDays)} days before due`}
                </span>
              );
            })()}
          </div>
        )}
        
        {/* Days Until Due - only show for unpaid invoices */}
        {isUnpaid && invoice.days_until_due !== null && invoice.days_until_due !== undefined && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Days:</span>
            <span className={`text-sm font-medium ${invoice.days_until_due >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {invoice.days_until_due >= 0
                ? `${invoice.days_until_due} before due`
                : `${Math.abs(invoice.days_until_due)} after due`
              }
            </span>
          </div>
        )}
        
        {/* Category - only show for unpaid invoices */}
        {isUnpaid && invoice.collection_reminder_category && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Category:</span>
            <span className="text-sm font-medium text-red-600 uppercase">
              {invoice.collection_reminder_category?.replace(/_/g, ' ') || 'N/A'}
            </span>
          </div>
        )}
        
        {/* Payment Reliability Score - only show for unpaid invoices */}
        {isUnpaid && paymentScore && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Reliability:</span>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-orange-600">
                {paymentScore.score}/10
              </span>
              <span className="text-xs text-gray-500">
                ({paymentScore.confidence})
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Agent Actions - only show for unpaid invoices */}
      {isUnpaid && (
        <div className="border-t border-gray-200 pt-3">
          <h5 className="text-sm font-medium text-gray-900 mb-2">Agent Actions</h5>

          {/* Last Analyzed */}
          {invoice.invoice_insights && invoice.invoice_insights.length > 0 && (
            <div className="mb-2">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Last analyzed:</span>
                <button
                  onClick={() => onViewAnalyses('Latest Invoice Insight', invoice.invoice_insights)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
                >
                  View
                </button>
              </div>
              <div className="text-xs text-gray-500">
                {invoice.invoice_insights[0].analyzed_at ? 
                  formatDate(invoice.invoice_insights[0].analyzed_at) : 
                  'Recently analyzed'
                }
              </div>
            </div>
          )}

          {/* Latest Reminder */}
          <div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Reminder Composed:</span>
              <div className="flex space-x-2">
                {/* WhatsApp Action */}
                {invoice.collection_reminder_whatsapp && invoice.collection_reminder_whatsapp.length > 0 && (
                  <button
                    onClick={() => onViewAnalyses('Latest WhatsApp Reminder', invoice.collection_reminder_whatsapp)}
                    className="text-green-600 hover:text-green-800 text-sm font-medium underline"
                  >
                    WhatsApp
                  </button>
                )}

                {/* Email Action */}
                {invoice.collection_reminder_email && invoice.collection_reminder_email.length > 0 && (
                  <button
                    onClick={() => onViewAnalyses('Latest Email Reminder', invoice.collection_reminder_email)}
                    className="text-purple-600 hover:text-purple-800 text-sm font-medium underline"
                  >
                    Email
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      <NotesModal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        entityType="invoice"
        entityId={invoice.invoice_id}
        entityName={`Invoice #${invoice.invoice_number || 'N/A'}`}
        organizationId={organizationId}
        onNoteCountChange={(delta: number) => setNotesCount(prev => Math.max(0, prev + delta))}
      />
    </div>
  );
};

export default InvoiceCard;
